/**
 * Form Progress Indicator
 * 
 * Shows progress through the pitch deck form steps.
 */

import { cn } from "@/lib/utils";
import { Check } from "lucide-react";

interface PitchDeckFormProgressProps {
  activeForm: 1 | 2;
  isForm1Complete: boolean;
  isForm2Complete: boolean;
  onFormChange: (form: 1 | 2) => void;
}

export function PitchDeckFormProgress({
  activeForm,
  isForm1Complete,
  isForm2Complete,
  onFormChange,
}: PitchDeckFormProgressProps) {
  const progressPercent = isForm2Complete ? 100 : isForm1Complete ? 75 : activeForm === 2 ? 50 : 25;

  return (
    <div className="space-y-4">
      {/* Progress Text */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">
          Form {activeForm} of 2
        </span>
        <span className="font-medium text-primary">
          {progressPercent}% Complete
        </span>
      </div>

      {/* Progress Bar */}
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-primary to-primary/80 transition-all duration-500"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      {/* Form Tabs */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onFormChange(1)}
          className={cn(
            "flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg border transition-all",
            activeForm === 1
              ? "bg-primary text-primary-foreground border-primary"
              : "bg-card hover:bg-muted border-border",
            isForm1Complete && activeForm !== 1 && "border-green-500"
          )}
        >
          {isForm1Complete ? (
            <Check className="h-4 w-4 text-green-500" />
          ) : (
            <span className={cn(
              "flex items-center justify-center h-5 w-5 rounded-full text-xs font-medium",
              activeForm === 1 
                ? "bg-primary-foreground/20 text-primary-foreground" 
                : "bg-muted-foreground/20 text-muted-foreground"
            )}>
              1
            </span>
          )}
          <span className="font-medium">Business Basics</span>
        </button>

        <button
          type="button"
          onClick={() => isForm1Complete && onFormChange(2)}
          disabled={!isForm1Complete}
          className={cn(
            "flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg border transition-all",
            activeForm === 2
              ? "bg-primary text-primary-foreground border-primary"
              : "bg-card hover:bg-muted border-border",
            !isForm1Complete && "opacity-50 cursor-not-allowed",
            isForm2Complete && activeForm !== 2 && "border-green-500"
          )}
        >
          {isForm2Complete ? (
            <Check className="h-4 w-4 text-green-500" />
          ) : (
            <span className={cn(
              "flex items-center justify-center h-5 w-5 rounded-full text-xs font-medium",
              activeForm === 2 
                ? "bg-primary-foreground/20 text-primary-foreground" 
                : "bg-muted-foreground/20 text-muted-foreground"
            )}>
              2
            </span>
          )}
          <span className="font-medium">Market & Model</span>
        </button>
      </div>
    </div>
  );
}
